/**
 * Min-max bucket downsampler for waveform rendering.
 *
 * Splits the series into buckets and keeps, per bucket, the sample with the
 * minimum y AND the sample with the maximum y (emitted in x order). This
 * preserves signal extrema (e.g. ECG QRS peaks) that plain striding or
 * averaging would clip.
 *
 * Pure function: never mutates its inputs.
 *
 * No-op (returns the original series) when the series already fits within
 * `maxPoints`.
 */
export function downsampleMinMax(
  xs: number[],
  ys: number[],
  maxPoints: number,
): { xs: number[]; ys: number[] } {
  const n = Math.min(xs.length, ys.length);

  if (n <= maxPoints) {
    return xs.length === n && ys.length === n
      ? { xs, ys }
      : { xs: xs.slice(0, n), ys: ys.slice(0, n) };
  }

  // Each bucket can emit up to 2 points (min + max), so use maxPoints / 2 buckets.
  const bucketCount = Math.max(1, Math.floor(maxPoints / 2));
  const outXs: number[] = [];
  const outYs: number[] = [];

  for (let bucket = 0; bucket < bucketCount; bucket++) {
    const start = Math.floor((bucket * n) / bucketCount);
    const end = Math.floor(((bucket + 1) * n) / bucketCount);
    if (start >= end) continue;

    let minIdx = start;
    let maxIdx = start;
    for (let i = start + 1; i < end; i++) {
      if (ys[i] < ys[minIdx]) minIdx = i;
      if (ys[i] > ys[maxIdx]) maxIdx = i;
    }

    if (minIdx === maxIdx) {
      outXs.push(xs[minIdx]);
      outYs.push(ys[minIdx]);
    } else {
      // Emit in x (index) order so the trace never doubles back.
      const first = Math.min(minIdx, maxIdx);
      const second = Math.max(minIdx, maxIdx);
      outXs.push(xs[first], xs[second]);
      outYs.push(ys[first], ys[second]);
    }
  }

  return { xs: outXs, ys: outYs };
}
